import { useState } from 'react'
import dayjs from 'dayjs'
import { useMutation, useQueryClient } from 'react-query'
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import ExpandRoundedIcon from '@mui/icons-material/ExpandRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded'
import { EditIcon } from 'components'
import { useBakersRecommendationService } from 'services/bakerRecomendationService'
import { useBakersInsightService } from 'services/bakersInsightService'
import { successToast } from 'utils/toastService'
import { TBakersInsight, TUpdateInsight } from 'types/models/BakersInsights'
import {
  RECOMMENDATION_INSIGHT,
  TBakersRecommendation,
  TUpdateRecommendation,
} from 'types/models/BakersRecommendations'
import * as sxStyles from '../../AnalyticsDetails.styled'

interface IRecommendationInsightCardProps {
  cardData: TBakersInsight | TBakersRecommendation
  type: RECOMMENDATION_INSIGHT
  selectedDeliveryId: string
}

// for long text we cut and add ...
const truncate = (text: string | undefined, max: number) => {
  if (!text) return ''
  if (text.length <= max) return text
  return `${text.slice(0, max)}...`
}

export const RecommendationInsightCard = ({
  cardData,
  type,
  selectedDeliveryId,
}: IRecommendationInsightCardProps) => {
  const titleMaxLength = 25
  const descriptionMaxLength = 35

  const [isEditing, setIsEditing] = useState<boolean>(false)

  const [titleValue, setTitleValue] = useState<string>(cardData.title ?? '')
  const [commentValue, setCommentValue] = useState<string>(
    'comment' in cardData ? (cardData.comment ?? '') : ''
  )
  const [acceptedValue, setAcceptedValue] = useState(
    'accepted' in cardData ? !!cardData.accepted : false
  )
  const queryClient = useQueryClient()

  const { updateRecommendationByGuid } = useBakersRecommendationService()
  const { updateInsightByGuid } = useBakersInsightService()
  const { mutate: updateInstance, isLoading: updateInstanceIsLoading } =
    useMutation(
      async (updatedInstance: TBakersInsight | TBakersRecommendation) => {
        if (type === RECOMMENDATION_INSIGHT.recommendation) {
          return updateRecommendationByGuid(
            updatedInstance.guid,
            updatedInstance as TUpdateRecommendation
          )
        }

        return updateInsightByGuid(
          updatedInstance.guid,
          updatedInstance as TUpdateInsight
        )
      },
      {
        onSuccess: (data) => {
          if (type === RECOMMENDATION_INSIGHT.recommendation) {
            successToast('Recommendation updated successfully')
            queryClient.invalidateQueries([
              'bakerRecommendations',
              selectedDeliveryId,
            ])
          } else {
            successToast('Insight updated successfully')
            queryClient.invalidateQueries(['bakerInsights', selectedDeliveryId])
          }
        },
      }
    )

  const handleEditClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    setIsEditing(true)
  }

  const handleSaveClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    setIsEditing(false)
    const updatedInstance = {
      ...cardData,
      title: titleValue,
      accepted: acceptedValue,
      comment: commentValue,
    }
    updateInstance(updatedInstance)
  }

  const handleExpandClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    // call expand model
  }

  const handleCloseClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    setIsEditing(false)
  }

  return (
    <Box sx={sxStyles.insightRecommendationSectionContainer}>
      <Box sx={sxStyles.insightRecommendationBox}>
        {/* Right side hover icons */}
        <Box
          className="card-actions"
          sx={sxStyles.insightRecommendationBoxHover}
        >
          <IconButton
            size="small"
            onClick={isEditing ? handleSaveClick : handleEditClick}
            sx={sxStyles.insightRecommendationHoverIcon}
          >
            {isEditing ? (
              <SaveRoundedIcon
                fontSize="small"
                sx={sxStyles.roundedIconColor}
              />
            ) : (
              <EditIcon color="#fff" />
            )}
          </IconButton>
          <IconButton
            size="small"
            sx={sxStyles.insightRecommendationHoverIcon}
            onClick={isEditing ? handleCloseClick : handleExpandClick}
          >
            {isEditing ? (
              <HighlightOffRoundedIcon
                fontSize="small"
                sx={sxStyles.roundedIconColor}
              />
            ) : (
              <ExpandRoundedIcon
                fontSize="small"
                sx={sxStyles.roundedIconColor}
              />
            )}
          </IconButton>
        </Box>
        {isEditing ? (
          <TextField
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            variant="standard"
            size="small"
            fullWidth
            InputProps={{
              sx: sxStyles.titleTypography,
            }}
          />
        ) : (
          <Typography sx={sxStyles.titleTypography} variant="subtitle1">
            {truncate(cardData.title, titleMaxLength)}
          </Typography>
        )}
        <Typography sx={sxStyles.subTitleTypography} variant="subtitle2">
          {truncate(cardData.description, descriptionMaxLength)}
        </Typography>
        <Typography sx={sxStyles.subTitleTypography} variant="body2">
          {dayjs(cardData.created_at).format('YYYY-MM-DD HH:mm')}
        </Typography>
        {isEditing && (
          <Box mt={1.5}>
            <TextField
              label="Comments"
              multiline
              fullWidth
              minRows={2}
              size="small"
              value={commentValue}
              onChange={(e) => setCommentValue(e.target.value)}
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  size="small"
                  checked={acceptedValue}
                  onChange={(e) => setAcceptedValue(e.target.checked)}
                />
              }
              label="Accepted"
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}
